/* What the corpus is actually doing: which files answer questions, and which are never
 * opened. Exported as pieces and rendered on the Documents page, under the form that adds to
 * the corpus it describes.
 */

import type { Coverage } from '../api/types';
import { BarChart } from '../charts/charts';
import { PanelCard } from '../components/Panel';
import { Table } from '../components/Table';
import { num } from '../lib/format';
import { useApp } from '../state/app';

/**
 * How often each document was reached. Documents never retrieved are not listed separately:
 * sorting the table below by times-used puts the zeroes on top.
 */
export function DocumentUse({ data }: { data: Coverage }) {
  const { simple, word } = useApp();

  return (
    <PanelCard
      title="Most-used documents"
      simpleTitle="Documents used most"
      hint="How often each file was retrieved to build an answer."
      simpleHint="How often each document was used. The ones at the top are worth keeping correct."
    >
      <BarChart
        label="Documents by how often they were retrieved"
        maxRows={12}
        rows={data.documents
          .filter((d) => d.hits)
          .map((d) => ({
            label: d.source,
            value: d.hits,
            detail: [
              { label: word('Retrieved', 'Used'), value: `${num(d.hits)}×` },
              { label: 'Conversations', value: num(d.conversations) },
              ...(simple ? [] : [{ label: 'Avg evidence', value: num(d.avg_confidence, 2) }]),
            ],
          }))}
      />
    </PanelCard>
  );
}

/** Every file, sortable — the reference the two cards above are summaries of. */
export function EveryDocument({ data }: { data: Coverage }) {
  // The row carries the corpus key — `unipass` — which is a folder name rather than the
  // product. Named through the session's table so a system registered later reads as itself.
  const { systemLabel } = useApp();

  return (
    <PanelCard title="Every document">
      <Table
        rows={data.documents}
        pageSize={10}
        search="Search documents"
        empty="No documents found on disk for this system."
        columns={[
          { key: 'source', label: 'Document' },
          {
            id: 'system',
            label: 'System',
            render: (r) => (r.system ? systemLabel(r.system) : '—'),
            sortValue: (r) => (r.system ? systemLabel(r.system) : ''),
          },
          { key: 'hits', label: 'Retrieved', simple: 'Times used', num: true },
          { key: 'conversations', label: 'Conversations', num: true },
          {
            id: 'avg_score',
            label: 'Avg score',
            num: true,
            advanced: true,
            render: (r) => num(r.avg_score, 2),
            sortValue: (r) => r.avg_score,
          },
          {
            id: 'size_kb',
            label: 'Size',
            num: true,
            advanced: true,
            render: (r) => `${num(r.size_kb, 1)} KB`,
            sortValue: (r) => r.size_kb,
          },
        ]}
      />
    </PanelCard>
  );
}
